import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import {
  Table,
  IconButton,
  ConfirmationModal,
  Box,
  Typography,
} from 'cs-kevlar-react'
import { StyledTableRow, StyledTableCell } from 'cs-kevlar-react/lib/es/table'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import { ColumnDef } from 'cs-kevlar-react/lib/es/table/base'
import { GetApp } from 'cs-kevlar-react/lib/es/icons'
import { ReportDateRange } from 'components/customers/ReportDateRange'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { PaginationOptions } from 'cs-kevlar-react/lib/es/table/Table'
import { useAuth } from 'hooks/useAuth'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const PageTitle = 'Reports'

interface Report extends IdentifiableModel {
  id: string
  name: string
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  hidePaginationBar: true,
  singularAdjective: 'report',
  pluralAdjective: 'reports',
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textSpacing: {
      marginLeft: theme.spacing(5),
      textAlign: 'left',
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  }),
)

const columns: ColumnDef<Report, string>[] = [
  {
    label: 'Reports',
    sortable: false,
    sortByDefault: false,
    filterable: false,
    dataProperty: 'name',
  },
]

enum MediaReportVersion {
  v1 = 1,
  v2 = 2,
}

const reports = [
  {
    id: 'DeviceInfo',
    name: 'Device Info',
  },
  {
    id: 'LicenseUsage',
    name: 'License Usage',
  },
  {
    id: 'Media',
    name: 'Media',
  },
  {
    id: 'LegacyMedia',
    name: 'Legacy Media',
  },
  {
    id: 'Customers',
    name: 'Customers',
  },
  {
    id: 'CustomerContacts',
    name: 'Customer Contacts',
  },
  {
    id: 'Connectors',
    name: 'Connectors',
  },
  {
    id: 'InvalidLicensesOrAdminUsers',
    name: 'Invalid Licenses or Admin Users',
  },
]

const Reports: React.FunctionComponent = () => {
  const classes = useStyles({})

  usePageViewAudit(PageTitle, true, true)

  const [
    showingMediaReportDatesModal,
    showMediaReportDatesModal,
  ] = React.useState({version: MediaReportVersion.v1, open: false})

  let defaultStartDate = new Date()
  defaultStartDate.setDate(defaultStartDate.getDate() - 30)
  const [startDate, setStartDate] = React.useState<Date | null>(
    defaultStartDate,
  )
  const [endDate, setEndDate] = React.useState<Date | null>(new Date())

  const [searchErrorText, setSearchErrorText] = React.useState<string | null>(
    null,
  )

  const downloadLink = (apiLink: string) => {
    const link = document.createElement('a')
    link.href = apiLink
    link.target = '_blank'
    link.click()
  }

  const onDownloadReport = (reportId: string) => {
    switch (reportId) {
      case 'DeviceInfo':
        downloadLink('api/reports/deviceInfo')
        return
      case 'LicenseUsage':
        downloadLink('api/reports/licenseUsage')
        return
      case 'Media':
        showMediaReportDatesModal({version: MediaReportVersion.v2, open: true})
        return
      case 'LegacyMedia':
        showMediaReportDatesModal({version: MediaReportVersion.v1, open: true})
        return
      case 'Customers':
        downloadLink('api/reports/customers')
        return
      case 'CustomerContacts':
        downloadLink('api/reports/customerContacts')
        return
      case 'Connectors':
        downloadLink('api/reports/connectors')
        return
      case 'InvalidLicensesOrAdminUsers':
        downloadLink('api/reports/invalidLicensesOrAdminUsers')
        return
    }
  }

  const onMediaReportConfirm = (version: MediaReportVersion) => {
    if (
      startDate === null ||
      isNaN(startDate.getTime()) ||
      endDate === null ||
      isNaN(endDate.getTime())
    ) {
      setSearchErrorText('Valid start and end dates must be provided.')
    } else if (startDate > endDate) {
      setSearchErrorText('The start date cannot be after the end date.')
    } else {
      if(version == MediaReportVersion.v1) {
        downloadLink(
          'api/reports/media/v1/' +
          startDate.toLocaleDateString().replace(/\//g, '-') +
          '/' +
          endDate.toLocaleDateString().replace(/\//g, '-'),
        )
      }
      else if (version == MediaReportVersion.v2) {
        downloadLink(
          'api/reports/media/v2/' +
          startDate.toLocaleDateString().replace(/\//g, '-') +
          '/' +
          endDate.toLocaleDateString().replace(/\//g, '-'),
        )
      }
      showMediaReportDatesModal({version: version, open: false})
      setSearchErrorText(null)
    }
  }

  return (
    <React.Fragment>
      <Table
        data={reports}
        columns={columns as ColumnDef<Report, string>[]}
        ariaLabel="Reports"
        paginationOptions={PAGINATION_OPTIONS}
        uniqueIdentifier="ReportsList"        
      >
        {(reports as Report[]).map(
            (row: Report, idx: number) => {
              return (
                <StyledTableRow key={`Table::Row::${row.id}`} idx={idx} hover>
                  {columns.map(
                    (columnDef: ColumnDef<Report, string>, colIdx: number) => {
                      const columnIdentifier =
                        columnDef.label === ''
                          ? colIdx.toString()
                          : columnDef.label
                      if (columnDef.dataProperty) {
                        return (
                          <StyledTableCell
                            key={`Table::Row::${row.id}::${columnIdentifier}`}
                            scope="row"
                            align={'left'}
                            padding={columnDef.padding}
                          >
                            <Box className={classes.row}>
                              <IconButton
                                onClick={() => onDownloadReport(row.id)}
                              >
                                <GetApp />
                              </IconButton>
                              <Typography className={classes.textSpacing}>
                                {(row as any)[columnDef.dataProperty]}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                        )
                      }
                    },
                  )}
                </StyledTableRow>
              )
            },
          )
        }
      </Table>

      <ConfirmationModal
        onCancel={() => showMediaReportDatesModal({version: showingMediaReportDatesModal.version, open: false})}
        onConfirm={()=> onMediaReportConfirm(showingMediaReportDatesModal.version)}
        open={showingMediaReportDatesModal.open}
        confirmText="Download Report"
        showCancelButton={false}
        title="Media Report"
        message={
          <ReportDateRange
            error={searchErrorText}
            startDate={startDate}
            endDate={endDate}
            onSelectStartDate={setStartDate}
            onSelectEndDate={setEndDate}
          />
        }
      />
    </React.Fragment>
  )
}

export const ReportsPage: PageDefinition = {
  routes: () => ['/reports'],
  type: [PageType.Support],
  sortOrder: () => 1,
  component: <Reports />,
  pageTitle: 'Reports',
  navLinkText: 'Reports',
  canAccess: (user) => {
    return user !== null && user.isSupport
  },
  buildNavLink: (user) => '/reports',
}
